import { classNames } from "primereact/utils";
import { Controller, useForm} from 'react-hook-form';
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { useState } from "react";
import QrCodePix from './QrCodePix';

interface SaldoClienteData {
  valor: number | null;
  tipoCompra: any;
  observacao?: string;
}

interface SaldoClienteCadastrarProps {
  onClose: () => void;
  onSave: (saldoCliente: any) => Promise<string>;
  showSuccess?: boolean;
  qrCodeData?: string;
}

export default function SaldoClienteCadastrar({ onClose, onSave, showSuccess, qrCodeData }: SaldoClienteCadastrarProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [tiposCompra] = useState([
    { label: 'Pix', value: 'PIX' }
  ]);

  const defaultValues: SaldoClienteData = {
    valor: null,
    tipoCompra: null
  };

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm({ defaultValues });

  const getFormErrorMessage = (name: string) => {
    const error = errors[name as keyof typeof errors];
    return error && <small className="p-error">{(error as any)?.message}</small>
  };

  const onSubmit = (data: SaldoClienteData) => {
    if (data.valor && data.tipoCompra) {
      setIsSubmitting(true);
      save(data);
    }
  };

  const save = async (data: SaldoClienteData) => {
    const saldoCliente = {
      valor: data.valor,
      tipoCompra: data.tipoCompra?.value || data.tipoCompra,
    };
    
    window.scrollTo(0, 0);
    try {
      await onSave(saldoCliente);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se tiver sucesso, mostrar tela de QR Code
  if (showSuccess && qrCodeData) {
    return (
      <div>
        <div className="card-header pb-3 border-bottom-1 surface-border">
          <div className="flex-consulta align-items-center gap-2">
            <i className="pi pi-check-circle text-2xl text-green-500"></i>
            <h3 className="m-0 text-900">Realize o pagamento!</h3>
          </div>
        </div>
        
        <QrCodePix 
              chavePix={qrCodeData} 
              showValor={true}
            />
        </div>
    
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="card-header pb-3 border-bottom-1 surface-border">
            <div className="flex-consulta align-items-center gap-2">
              <i className="pi pi-shopping-cart text-2xl text-primary"></i>
              <h3 className="m-0 text-900">Nova Compra de Saldo</h3>
            </div>
          </div>
          
          <div className="pt-4">
            <div className="grid">
              {/* Valor da Compra */}
              <div className="col-12 md:col-6">
                <div className="field">
                  <Controller
                    name="valor"
                    control={control}
                    rules={{ 
                      required: 'Valor é obrigatório',
                      min: { value: 1, message: 'Valor deve ser maior que zero' }
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <label htmlFor={field.name} className="font-semibold text-900 block mb-2">
                          Valor da Compra (R$) <span className="text-red-500">*</span>
                        </label>
                        <InputNumber
                          id={field.name}
                          value={field.value}
                          onValueChange={(e) => field.onChange(e.value)}
                          mode="currency"
                          currency="BRL"
                          locale="pt-BR"
                          className={classNames('w-full p-inputtext-lg', { 'p-invalid': fieldState.error })}
                          min={0}
                          max={999999}
                          placeholder="R$ 0,00"
                        />
                        {getFormErrorMessage(field.name)}
                      </>
                    )}
                  />
                </div>
              </div>

              {/* Tipo de Compra */}
              <div className="col-12 md:col-6">
                <div className="field">
                  <Controller
                    name="tipoCompra"
                    control={control}
                    rules={{ required: 'Tipo de compra é obrigatório' }}
                    render={({ field, fieldState }) => (
                      <>
                        <label htmlFor={field.name} className="font-semibold text-900 block mb-2">
                          Tipo de Compra <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          options={tiposCompra}
                          optionLabel="label"
                          placeholder="Selecione o tipo de compra"
                          className={classNames('w-full p-inputtext-lg', { 'p-invalid': fieldState.error })}
                        />
                        {getFormErrorMessage(field.name)}
                      </>
                    )}
                  />
                </div>
              </div>

            </div>

            {/* Informações Adicionais */}
            <div style={{ marginTop: '1.5rem' }}>
              <div className="p-4 bg-blue-50 border-round border-1 border-blue-200">
                <div className="flex-consulta align-items-center gap-2 mb-3">
                  <i className="pi pi-info-circle text-blue-600 text-lg"></i>
                  <h5 className="m-0 text-blue-900">Informações da Compra</h5>
                </div>
                <div className="flex-consulta flex-column gap-3">
                  <div className="flex-consulta align-items-center gap-2">
                    <i className="pi pi-check-circle text-blue-600"></i>
                    <span className="text-sm text-700">O saldo será creditado após a confirmação do pagamento</span>
                  </div>
                  <div className="flex-consulta align-items-center gap-2">
                    <i className="pi pi-clock text-blue-600"></i>
                    <span className="text-sm text-700">Tempo estimado de processamento: até 2 horas úteis</span>
                  </div>
                  <div className="flex-consulta align-items-center gap-2">
                    <i className="pi pi-shield text-blue-600"></i>
                    <span className="text-sm text-700">Transação segura e criptografada</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex-consulta justify-content-end align-items-center gap-2 border-top-1 surface-border" style={{ marginTop: '2rem', paddingTop: '1.5rem' }}>
              <Button
                label="Cancelar"
                icon="pi pi-times"
                outlined
                severity="secondary"
                size="small"
                style={{ minWidth: '120px', height: '40px' }}
                onClick={onClose}
              />
              <Button
                label="Confirmar Compra"
                icon="pi pi-check"
                type="submit"
                size="small"
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{ 
                  backgroundColor: '#183462', 
                  border: 'none',
                  minWidth: '160px',
                  height: '40px'
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}